import { useMemo, useState } from "react";
import { Autocomplete } from "@/components/ui/autocomplete";
import { Label } from "@/components/ui/label";

/** Searchable combobox for saved models (never chips): title + short description per row. */
export function ModelSearchField<T>({
  label,
  placeholder,
  items,
  getKey,
  getTitle,
  getDescription,
  onPick,
  emptyText,
  loading = false,
  className,
}: {
  label: string;
  placeholder: string;
  items: T[];
  getKey: (item: T) => string;
  getTitle: (item: T) => string;
  getDescription?: (item: T) => string;
  onPick: (item: T) => void;
  emptyText: string;
  loading?: boolean;
  className?: string;
}) {
  const [search, setSearch] = useState("");
  const byKey = useMemo(() => new Map(items.map((item) => [getKey(item), item])), [items, getKey]);
  const options = useMemo(
    () => items.map((item) => ({ label: getTitle(item), value: getKey(item) })),
    [items, getKey, getTitle],
  );
  const id = `model-search-${label}`;

  return (
    <div className={className}>
      <Label htmlFor={id} className="mb-2 block text-sm font-medium">
        {label}
      </Label>
      <Autocomplete
        options={options}
        value={search}
        onChange={setSearch}
        onSelect={(option) => {
          const picked = byKey.get(option.value);
          if (!picked) return;
          onPick(picked);
          setSearch("");
        }}
        placeholder={placeholder}
        emptyMessage={loading ? "…" : emptyText}
        popoverPortalled={false}
        renderOption={(option) => {
          const item = byKey.get(option.value);
          const description = item && getDescription ? getDescription(item) : "";
          return (
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-medium">{option.label}</span>
              {description ? <span className="truncate text-xs text-muted-foreground">{description}</span> : null}
            </div>
          );
        }}
      />
    </div>
  );
}
